import { getToken } from "@/utils/auth";
import { emitter } from "../mitt";
import { useUserStore } from "@/store/modules/user";
import { HTTP_HEADERS } from "@/config/httpHead";

/**
 * 全局余额 WebSocket 管理器
 * 用于实时监听余额变化，支持跨浏览器同步
 */
class GlobalBalanceWebSocket {
  private ws: WebSocket | null = null;
  private reconnectTimer: any = null;
  private reconnectCount = 0;
  private maxReconnectAttempts = 5;
  private reconnectInterval = 3000;
  private isDestroyed = false;
  private storageListener: ((event: StorageEvent) => void) | null = null;

  // 连接状态变化回调
  public onConnectionStatusChange: ((connected: boolean) => void) | null = null;

  /**
   * 处理直播开始
   */
  private async handleLiveStart() {
    // 🚀 直播开始时，确保计费 WebSocket 连接
    if (!this.isConnected()) {
      await this.connect();
    } else {
    }
  }

  /**
   * 处理直播结束
   */
  private handleLiveEnd() {
    // 🚀 关闭直播间时不需要关闭计费接口，保持连接
    // 不再自动断开计费接口连接
  }

  /**
   * 连接 WebSocket
   */
  connect(): Promise<boolean> {
    return new Promise((resolve, reject) => {
      // 🔧 修复：如果已经连接，直接返回成功
      if (this.isConnected()) {
        resolve(true);
        return;
      }

      // 🔧 如果正在连接中，等待连接完成
      if (this.ws && this.ws.readyState === WebSocket.CONNECTING) {
        // 监听连接状态变化
        const handleOpen = () => {
          this.ws?.removeEventListener("open", handleOpen);
          this.ws?.removeEventListener("error", handleError);
          resolve(true);
        };

        const handleError = () => {
          this.ws?.removeEventListener("open", handleOpen);
          this.ws?.removeEventListener("error", handleError);
          resolve(false);
        };

        this.ws.addEventListener("open", handleOpen);
        this.ws.addEventListener("error", handleError);
        return;
      }

      const token = getToken();
      if (!token) {
        resolve(false);
        return;
      }

      // 🔧 只有在连接已关闭或不存在时才关闭现有连接
      if (this.ws && this.ws.readyState === WebSocket.CLOSED) {
        this.ws = null;
      } else if (this.ws) {
        this.ws.close();
        this.ws = null;
      }

      const wsProtocol = window.location.protocol === "https:" ? "wss:" : "ws:";
      const wsHost = window.location.host;
      const userId = useUserStore().userInfo?.id;
      let wsUrl = "";
      if (import.meta.env.VITE_API_BASE_WS) {
        wsUrl = `${import.meta.env.VITE_API_BASE_WS}/live/live-account?token=${token}&userId=${userId}`;
      } else {
        wsUrl = `${wsProtocol}//${wsHost}${HTTP_HEADERS.Ws}/live/live-account?token=${token}&userId=${userId}`;
      }

      try {
        this.ws = new WebSocket(wsUrl);

        this.ws.onopen = () => {
          this.reconnectCount = 0;
          console.log("建立ws计费链接");
          // const query = JSON.stringify({ ws: "domeker" });
          this.ws.send("domeker");
          // 🚀 通知连接状态变化
          if (this.onConnectionStatusChange) {
            this.onConnectionStatusChange(true);
          }

          // 🚀 发出连接状态事件
          emitter.emit("balanceWebSocketStatus", {
            status: "connected",
            timestamp: Date.now()
          });

          resolve(true);
        };

        this.ws.onmessage = event => {
          try {
            console.log("📨 收到余额WebSocket原始数据:", event.data);
            const data = JSON.parse(event.data);
            console.log("📨 解析后的数据:", data);

            // 处理关闭消息
            if (data === "关闭推送Websocket！！") {
              console.log("🔌 收到关闭推送消息");
              return;
            }

            // 🚀 检查特定错误消息：未找到未结束的直播会话
            if (
              data.error &&
              data.error === "推送失败: 未找到未结束的直播会话"
            ) {
              console.log("❌ 未找到未结束的直播会话，主动关闭连接");
              // 主动关闭连接
              this.disconnect();
              return; // 不继续处理其他逻辑
            }
            // 处理余额更新消息
            const { account, remainder, requiredAmount } = data;
            if (remainder === -0.1) {
              emitter.emit("balanceUpdate", {
                cost: requiredAmount,
                remainder: remainder,
                account: account
              });
              return this.disconnect();
            }
            if (!requiredAmount && !remainder && !account)
              return emitter.emit("updateUserBalance");
            console.log(
              "💰 提取的余额数据 - account:",
              account,
              "remainder:",
              remainder,
              "requiredAmount:",
              requiredAmount
            );

            // 通过事件总线广播余额更新
            const updateData = {
              cost: requiredAmount || 0,
              remainder: remainder || 0,
              account: account || ""
            };
            console.log("📡 准备发送balanceUpdate事件:", updateData);
            emitter.emit("balanceUpdate", updateData);
          } catch (error) {
            console.error("❌ 处理余额WebSocket消息失败:", error);
          }
        };

        this.ws.onerror = error => {
          // 通知连接状态变化
          if (this.onConnectionStatusChange) {
            this.onConnectionStatusChange(false);
          }

          // 发出错误状态事件
          emitter.emit("balanceWebSocketStatus", {
            status: "error",
            timestamp: Date.now(),
            error
          });

          // 静默处理错误，不抛出异常
          this.attemptReconnect();
          resolve(false);
        };

        this.ws.onclose = _event => {
          this.ws = null;

          // 🚀 通知连接状态变化
          if (this.onConnectionStatusChange) {
            this.onConnectionStatusChange(false);
          }

          // 🚀 发出断开状态事件
          emitter.emit("balanceWebSocketStatus", {
            status: "disconnected",
            timestamp: Date.now()
          });

          // 如果不是主动关闭且未达到最大重连次数，则重连
          if (
            !this.isDestroyed &&
            this.reconnectCount < this.maxReconnectAttempts
          ) {
            this.scheduleReconnect();
          }
        };
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * 断开连接
   */
  disconnect() {
    this.isDestroyed = true;

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    // 🚀 发出关闭事件，确保所有浏览器标签页都能收到
    emitter.emit("balanceWebSocketClose", {
      reason: "主动断开连接",
      timestamp: Date.now()
    });

    // 🌐 跨浏览器同步断开状态
    try {
      localStorage.setItem(
        "balanceWebSocketClose",
        JSON.stringify({
          reason: "主动断开连接",
          timestamp: Date.now()
        })
      );
    } catch (error) {}

    // 移除跨浏览器监听器
    this.removeCrossBrowserListener();
  }

  /**
   * 内部断开连接方法（用于跨浏览器同步）
   */
  _internalDisconnect(reason: string) {
    this.isDestroyed = true;

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    // 🚀 发出关闭事件，隐藏计费气泡
    emitter.emit("balanceWebSocketClose", {
      reason,
      timestamp: Date.now()
    });

    // 🚀 通知连接状态变化
    if (this.onConnectionStatusChange) {
      this.onConnectionStatusChange(false);
    }
  }

  /**
   * 🔧 新增：静默断开连接方法（不触发事件，避免循环）
   */
  _silentDisconnect(reason: string) {
    this.isDestroyed = true;

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    // 🚀 只通知连接状态变化，不发出关闭事件
    if (this.onConnectionStatusChange) {
      this.onConnectionStatusChange(false);
    }

    // 🚀 发出断开状态事件（用于UI更新）
    emitter.emit("balanceWebSocketStatus", {
      status: "disconnected",
      timestamp: Date.now()
    });
  }

  /**
   * 检查是否已连接
   */
  isConnected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }

  /**
   * 重置状态
   */
  reset() {
    this.isDestroyed = false;
    this.reconnectCount = 0;
  }

  /**
   * 尝试重连
   */
  private attemptReconnect() {
    if (this.isDestroyed || this.reconnectCount >= this.maxReconnectAttempts) {
      return;
    }

    // 检查WebSocket状态，如果已经关闭则清理
    if (this.ws && this.ws.readyState === WebSocket.CLOSED) {
      this.ws = null;
    }

    this.reconnectCount++;
    this.scheduleReconnect();
  }

  /**
   * 安排重连
   */
  private scheduleReconnect() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }

    this.reconnectTimer = setTimeout(() => {
      if (!this.isDestroyed) {
        this.connect().catch(error => {});
      }
    }, this.reconnectInterval);
  }

  /**
   * 初始化跨浏览器监听器
   */
  initCrossBrowserListener() {
    // 移除之前的监听器
    this.removeCrossBrowserListener();

    // 创建新的监听器
    this.storageListener = (event: StorageEvent) => {
      if (event.key === "liveStatusChange") {
        try {
          const data = JSON.parse(event.newValue || "{}");
          if (data.action === "start") {
            this.handleLiveStart();
          } else if (data.action === "end") {
            this.handleLiveEnd();
          }
        } catch {}
      } else if (event.key === "balanceWebSocketClose") {
        try {
          const data = JSON.parse(event.newValue || "{}");
          // 🔧 修复：跨浏览器同步关闭，使用静默关闭避免循环触发
          if (this.isConnected()) {
            this._silentDisconnect(data.reason || "跨浏览器同步关闭");
          }
        } catch (error) {}
      }
    };

    // 添加监听器
    window.addEventListener("storage", this.storageListener);
  }

  /**
   * 移除跨浏览器监听器
   */
  removeCrossBrowserListener() {
    if (this.storageListener) {
      window.removeEventListener("storage", this.storageListener);
      this.storageListener = null;
    }
  }
}

export const globalBalanceWebSocket = new GlobalBalanceWebSocket();
